import {
  CalendarOutlined,
  ContactsOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  PlusOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  List,
  Popconfirm,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { ProjectStatus } from "../../enum/common";
import { IProductionProjectMember } from "../../models/Production";
import {
  useDeleteProductionProjectMember,
  useGetProductionProjectByAlias,
  useGetProductionProjectMember,
} from "../../query/ProductionQuery";
import { notify } from "../../utils/toast";
import MyAvatar from "../common/Avatar";
import ModalAddProductionMember from "../modal/ModalAddProductionMember";
import ModalEditProductionMember from "../modal/ModalEditProductionMember";
import ModalEditProductionProject from "../modal/ModalEditProductionProject";

const { Title, Text, Paragraph } = Typography;

interface IProductionProjectInfoTab {
  alias: string;
}

const ProductionProjectInfoTab = ({ alias }: IProductionProjectInfoTab) => {
  const [isModalAddMemberOpen, setIsModalAddMemberOpen] =
    useState<boolean>(false);
  const [isModalEditMemberOpen, setIsModalEditMemberOpen] =
    useState<boolean>(false);
  const [isModalEditProjectOpen, setIsModalEditProjectOpen] =
    useState<boolean>(false);
  const [selectedMember, setSelectedMember] =
    useState<IProductionProjectMember | null>(null);
  const { data: project, isLoading } = useGetProductionProjectByAlias(alias!);
  const { data: prdProjecMember, isLoading: prdProjecMemberLoading } =
    useGetProductionProjectMember(project?.id!);
  const deleteMemberMutation = useDeleteProductionProjectMember();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <Spin size="large" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-4">
        <Text type="secondary">Không tìm thấy thông tin dự án</Text>
      </div>
    );
  }

  const getStatusTag = (status: ProjectStatus) => {
    const statusConfig = {
      [ProjectStatus.NEW]: { color: "blue", text: "Mới" },
      [ProjectStatus.IN_PROGRESS]: { color: "orange", text: "Đang thực hiện" },
      [ProjectStatus.DONE]: { color: "green", text: "Hoàn thành" },
      [ProjectStatus.CANCELLED]: { color: "red", text: "Đã hủy" },
    };
    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>Trạng thái: {config.text}</Tag>;
  };

  const calculateDaysRemaining = () => {
    const deadline = dayjs(project.deadline);
    const today = dayjs();
    const diff = deadline.diff(today, "day");
    return diff;
  };

  const daysRemaining = calculateDaysRemaining();

  const handleEditMember = (member: IProductionProjectMember) => {
    setSelectedMember(member);
    setIsModalEditMemberOpen(true);
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={18} xl={18}>
        <Card
          className="mb-4"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Space direction="vertical" size={4}>
                <Space>
                  <Tag color="green">Mã dự án: {project.project}</Tag>
                  {getStatusTag(project.status)}
                </Space>
                <Title level={3} style={{ margin: 0, color: "pink" }}>
                  Dự án: {project.projectName}
                </Title>

                <Paragraph style={{ color: "white", marginTop: 10 }}>
                  Mô tả: {project.projectDescription || "Chưa có mô tả dự án"}
                </Paragraph>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsModalEditProjectOpen(true)}
                size="large"
                style={{
                  background:
                    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
                  border: "none",
                  color: "#d4380d",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(252, 182, 159, 0.4)",
                }}
              >
                Chỉnh sửa dự án
              </Button>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Ngày yêu cầu"
                value={dayjs(project.requestDate).format("DD/MM/YYYY")}
                prefix={<CalendarOutlined />}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Ngày khởi chạy"
                value={dayjs(project.startDate).format("DD/MM/YYYY")}
                prefix={<CalendarOutlined />}
                valueStyle={{ fontSize: 18, color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Hạn hoàn thành"
                value={dayjs(project.deadline).format("DD/MM/YYYY")}
                prefix={<CalendarOutlined />}
                valueStyle={{ fontSize: 18, color: "#ff4d4f" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Còn lại"
                value={daysRemaining}
                suffix="ngày"
                valueStyle={{
                  fontSize: 18,
                  color:
                    daysRemaining < 0
                      ? "#ff4d4f"
                      : daysRemaining < 7
                        ? "#faad14"
                        : "#52c41a",
                }}
              />
            </Card>
          </Col>
        </Row>
        <Card style={{ borderLeft: "4px solid #52c41a" }} className="mb-4">
          <Statistic
            title="Đã chi tiêu"
            value={project.totalSpent ? Number(project.totalSpent) : 0}
            precision={0}
            valueStyle={{ color: "#52c41a", fontSize: 24 }}
            prefix={<DollarOutlined />}
            suffix={project.totalSpent ? "VND" : ""}
          />
          {!project.totalSpent && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Chưa có dữ liệu
            </Text>
          )}
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card
              title={
                <Space>
                  <ContactsOutlined style={{ color: "#1890ff" }} />
                  <span>Thông tin khách hàng</span>
                </Space>
              }
              className="h-full"
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <div>
                  <Text type="secondary">
                    <EnvironmentOutlined /> Khách hàng
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: 16 }}>
                    {project.customerName}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">
                    <PhoneOutlined /> Liên hệ
                  </Text>
                  <br />
                  {(() => {
                    try {
                      const contacts = JSON.parse(project.contactProject);
                      return (
                        <div>
                          {contacts.map((contact: any, index: number) => (
                            <div key={index} style={{ marginBottom: 8 }}>
                              <Text strong style={{ fontSize: 16 }}>
                                Tên: {contact.name}
                              </Text>
                              <br />
                              <Text type="secondary">
                                SDT: {contact.phone} - VT: {contact.position}
                              </Text>
                            </div>
                          ))}
                        </div>
                      );
                    } catch {
                      return (
                        <Text strong style={{ fontSize: 16 }}>
                          {project.contactProject}
                        </Text>
                      );
                    }
                  })()}
                </div>
                <div>
                  <Text type="secondary">
                    <UserOutlined /> Yêu cầu từ
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: 16 }}>
                    {project.requestFrom}
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card
              title={
                <Space>
                  <TeamOutlined style={{ color: "#52c41a" }} />
                  <span>Đội ngũ phụ trách</span>
                </Space>
              }
              className="h-full"
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <div>
                  <Text type="secondary">
                    <TeamOutlined /> Sale phụ trách
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: 16 }}>
                    {project.saleInCharge}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">
                    <TeamOutlined /> Sale Admin phụ trách
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: 16 }}>
                    {project.saleAdInCharge}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">
                    <ShoppingOutlined /> Mua hàng phụ trách
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: 16 }}>
                    {project.purchaseInCharge}
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </Col>
      <Col xs={24} lg={6} xl={6}>
        <Card
          title={
            <Space>
              <TeamOutlined style={{ color: "#722ed1" }} />
              <Text>
                Thành viên dự án (<span>{prdProjecMember?.length})</span>
              </Text>
            </Space>
          }
          extra={
            <Tooltip title="Thêm thành viên">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={() => setIsModalAddMemberOpen(true)}
              />
            </Tooltip>
          }
          className="h-full"
        >
          {prdProjecMemberLoading ? (
            <div className="flex justify-center items-center p-4">
              <Spin />
            </div>
          ) : prdProjecMember && prdProjecMember.length > 0 ? (
            <List
              dataSource={prdProjecMember}
              renderItem={(member) => (
                <List.Item
                  key={member.id}
                  actions={[
                    <Tooltip title="Chỉnh sửa" key="edit">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEditMember(member)}
                      />
                    </Tooltip>,
                    <Tooltip title="Xóa" key="delete">
                      <Popconfirm
                        title="Xác nhận xóa"
                        description={`Bạn có chắc chắn muốn xóa thành viên ${member.fullName}?`}
                        onConfirm={async () => {
                          try {
                            await deleteMemberMutation.mutateAsync(member.id);
                            notify.success("Xóa thành viên thành công");
                          } catch (error) {
                            notify.error("Có lỗi xảy ra");
                          }
                        }}
                      >
                        <Button
                          size="small"
                          type="text"
                          loading={deleteMemberMutation.isPending}
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<MyAvatar name={member.fullName} />}
                    title={
                      <Space direction="vertical" size={0}>
                        <Text strong>{member.fullName}</Text>
                        <Tag color="purple" style={{ fontSize: 11 }}>
                          {member.role}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <PhoneOutlined /> {member.phone}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <div className="text-center p-4">
              <Text type="secondary">Chưa có thành viên nào</Text>
              <br />
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => setIsModalAddMemberOpen(true)}
              >
                Thêm thành viên đầu tiên
              </Button>
            </div>
          )}
        </Card>
      </Col>

      <ModalAddProductionMember
        open={isModalAddMemberOpen}
        onCancel={() => setIsModalAddMemberOpen(false)}
        projectId={project?.id!}
      />

      <ModalEditProductionMember
        open={isModalEditMemberOpen}
        onCancel={() => {
          setIsModalEditMemberOpen(false);
          setSelectedMember(null);
        }}
        member={selectedMember}
      />

      <ModalEditProductionProject
        open={isModalEditProjectOpen}
        onCancel={() => setIsModalEditProjectOpen(false)}
        project={project}
      />
    </Row>
  );
};

export default ProductionProjectInfoTab;
